<template>
    <div class="review">
        <div class="review__header">
            <div class="review__avatar">
                <Avatar :url="avatarUrl" :webp-url="webpAvatarUrl" :name="name" size="medium" />
            </div>
            <div class="review__info">
                <div>
                    <Ratings :value="overallScore" class="review__ratings" />
                    <div class="review__reviewed-at">{{ reviewedAt }}</div>
                </div>
                <div class="review__name">{{ name }}</div>
            </div>
        </div>
        <p class="review__comment">
            {{ overallComment }}
        </p>
        <GoogleTranslateLink
            v-if="overallComment && showGoogleTranslate"
            :text="overallComment"
            :label="'common.get_the_translation'"
            class="review__google-translate"
        />
    </div>
</template>

<script>
import Avatar from '~/components/Avatar.vue';
import Ratings from '~/components/Ratings.vue';
import GoogleTranslateLink from '~/components/GoogleTranslateLink.vue';

export default {
    components: {
        Avatar,
        Ratings,
        GoogleTranslateLink,
    },
    props: {
        review: {
            type: Object,
            default: () => {},
        },
        showReviewee: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        reviewedAt() {
            return this.review.createdAt ? this.$options.filters.dateFormat(this.review.createdAt, 'MMM yyyy') : '';
        },
        name() {
            if (this.showReviewee) {
                return this.review.revieweeName || null;
            }

            if (this.review.reviewerName) {
                return this.review.reviewerName;
            }

            return '––';
        },
        avatarUrl() {
            if (this.showReviewee) {
                return this.review.revieweeAvatarUrl || null;
            }

            return this.review.reviewerAvatarUrl || null;
        },
        webpAvatarUrl() {
            if (this.showReviewee) {
                return this.review.revieweeWebpAvatarUrl || null;
            }

            return this.review.reviewerWebpAvatarUrl || null;
        },
        overallScore() {
            return this.review.overallScore;
        },
        overallComment() {
            return this.review.overallComment;
        },
        showGoogleTranslate() {
            const activeLanguage = new Intl.Locale(this.$i18n.localeProperties.iso).language;

            return activeLanguage !== this.review.reviewerLanguage;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.review {
    padding: 16px;
    border: 1px solid $neutral-90;
    border-radius: 8px;

    @include font-size(16px);

    &__avatar {
        margin-right: 16px;
        width: 60px;
        height: 60px;

        @include lg {
            width: 40px;
            height: 40px;

            ::v-deep .avatar {
                min-width: 40px;
                max-width: 40px;
                min-height: 40px;
                max-height: 40px;
            }
        }
    }

    &__header {
        display: flex;
    }

    &__info {
        > div:first-child {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
        }
    }

    &__name {
        font-weight: bold;
        display: inline-block;
    }

    &__comment {
        margin-bottom: 0;
        margin-top: 16px;
    }

    &__google-translate {
        display: block;
        margin-top: 16px;
        text-align: right;
    }

    &__reviewed-at {
        color: $secondary-text-color;
        @include font-size(14px);
    }

    &__ratings {
        border-right: 1px solid $neutral-80;
        padding-right: 8px;
        margin-right: 8px;

        ::v-deep .ratings__icon.svg-icon {
            width: 24px;
            height: 24px;
        }
    }
}
</style>
